import { AnimatePresence, motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import type { Category, Item, Member } from '../../lib/supabase'
import { tintVar } from '../../lib/categoryTint'
import { useItemHistory } from '../../hooks/useItemHistory'
import { useTrips } from '../../hooks/useTrips'
import { useStores, useTripCost } from '../../hooks/useShopping'
import { useTripEdits } from '../../hooks/useTripEdits'
import { TripSummary, type FinishedTrip } from '../list/TripSummarySheet'
import { SheetPanel } from '../SheetPanel'
import { TripEditor } from './TripEditor'

/** A trip's summary (Edit trip one tap away), or its editor straight away (Add cost). */
export type TripSheet = { summary: FinishedTrip } | { tripId: string }

const EASE = [0.2, 0.8, 0.2, 1] as const

// The one trip sheet, wherever a trip opens: Home's Last trip (its card, and
// Add cost), the summary after Finish trip, and Stats › Trips. Two faces in
// one panel — the summary and Edit trip — that slide into each other; Edit
// trip is there for any trip with a row (046), whoever finished it.
export function TripSheets({ sheet, showSummary, zIndex, onClose, ...data }: {
  sheet: TripSheet | null
  householdId: string
  items: Item[]
  categories: Category[]
  members: Member[]
  selfMemberId: string | null
  /** Settings › Trip summary (after Finish trip): off leaves only the cost question. */
  showSummary?: boolean
  /** Raised over another sheet (the stats page). */
  zIndex?: number
  onClose: () => void
}) {
  const key = !sheet ? '' : 'tripId' in sheet ? `edit:${sheet.tripId}` : `trip:${sheet.summary.tripId ?? sheet.summary.finishedAt.toISOString()}`
  return (
    <AnimatePresence>
      {sheet && <Faces key={key} sheet={sheet} showSummary={showSummary} zIndex={zIndex} onClose={onClose} {...data} />}
    </AnimatePresence>
  )
}

function Faces({ sheet, householdId, items, categories, members, selfMemberId, showSummary, zIndex, onClose }: {
  sheet: TripSheet
  householdId: string
  items: Item[]
  categories: Category[]
  members: Member[]
  selfMemberId: string | null
  showSummary?: boolean
  zIndex?: number
  onClose: () => void
}) {
  const summary = 'summary' in sheet ? sheet.summary : null
  const tripId = summary ? summary.tripId : (sheet as { tripId: string }).tripId
  const [face, setFace] = useState<'summary' | 'edit'>(summary ? 'summary' : 'edit')
  const setCost = useTripCost(householdId)
  const { editTrip, deleteTrip } = useTripEdits(householdId)
  // The editor takes the order walked from the history once, when it opens: it waits for it.
  const { rows, loaded } = useItemHistory(householdId, items)
  const { trips } = useTrips(householdId)
  const stores = useStores(householdId)
  const row = tripId ? trips.find(t => t.id === tripId && !t.deleted_at) ?? null : null
  const cost = row?.cost != null && row.currency ? { cost: row.cost, currency: row.currency } : null
  const byId = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const nameOf = (id: string | null) => (id && byId.get(id)?.name) || 'Other'
  const tintFor = (id: string | null) => tintVar(id ? byId.get(id) : null)
  // The summary sits left of the editor: Edit slides in from the right, Back returns.
  const slide = (x: number) => ({ initial: { opacity: 0, x }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x }, transition: { duration: 0.2, ease: EASE } })

  return (
    <SheetPanel onClose={onClose} ariaLabel={face === 'edit' ? 'Edit trip' : 'Trip summary'} maxHeight="92dvh" layout="scroll" zIndex={zIndex}>
      <AnimatePresence mode="popLayout" initial={false}>
        {face === 'summary' && summary ? (
          <motion.div key="summary" {...slide(-24)}>
            {/* Remounted once when the trips row lands, so the field starts from its cost. */}
            <TripSummary key={row ? 'row' : 'none'} trip={summary} cost={cost} members={members} selfMemberId={selfMemberId} nameOf={nameOf} tintFor={tintFor}
              showSummary={showSummary} onCost={setCost} onEdit={row ? () => setFace('edit') : undefined} onClose={onClose} />
          </motion.div>
        ) : (
          <motion.div key="edit" {...slide(24)}>
            {row && loaded
              ? <TripEditor trip={row} rows={rows} stores={stores} tintFor={tintFor} focusCost={!summary}
                onBack={summary ? () => setFace('summary') : undefined}
                onEdit={editTrip} onDelete={deleteTrip} onCost={setCost} onClose={onClose} />
              : <p className="ml-caption">Loading…</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </SheetPanel>
  )
}
